import React from 'react';
import { Lock } from 'lucide-react';
import { AnimatedValue } from './AnimatedValue';
import { confidenceTier, type DimensionDisplayData } from './dimensionHelpers';
import { DimensionMeter } from '../profile/DimensionMeter';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';

interface DimensionCardProps {
  dim: DimensionDisplayData;
  index: number;
  visible: boolean;
  animate: boolean;
  /** Inline style overrides for FLIP animation */
  flightStyle?: React.CSSProperties;
  cardRef?: (el: HTMLDivElement | null) => void;
  onClick?: (dim: DimensionDisplayData) => void;
  /** Apply the dashboard bento "thick slab" treatment instead of the default surface. */
  bento?: boolean;
}

export const DimensionCard = React.memo(function DimensionCard({
  dim, index, visible, animate, flightStyle, cardRef, onClick, bento,
}: DimensionCardProps) {
  // Emphasis marks a CLEAR result — evidence that one option genuinely dominated —
  // never magnitude. A preference scale has no good end.
  const isHigh = dim.clarity === 'clear';

  if (dim.locked) {
    return (
      <div
        ref={cardRef}
        className={
          bento
            ? `${THICK_TILE_CLASSNAME} p-5 transition-all duration-700`
            : 'relative overflow-hidden rounded-2xl border border-dark-border bg-dark-surface p-5 transition-all duration-700'
        }
        style={{
          opacity: visible ? 0.5 : 0,
          transform: visible ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
          transitionDelay: `${index * 120}ms`,
          ...(bento ? THICK_TILE_STYLE : null),
          ...flightStyle,
        }}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 rounded-full bg-dark-border" />
            <span className="text-sm font-semibold text-content-muted">{dim.name}</span>
          </div>
          <Lock size={16} className="text-content-dim" />
        </div>
        <div className="text-xs text-content-dim">Upgrade to unlock</div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={
        bento
          ? `${THICK_TILE_CLASSNAME}${onClick ? ' cursor-pointer hover:brightness-110' : ''}`
          : `relative overflow-hidden rounded-2xl bg-dark-surface${onClick ? ' cursor-pointer hover:brightness-110' : ''}`
      }
      style={{
        opacity: visible && !flightStyle ? 1 : !visible ? 0 : undefined,
        transform: visible && !flightStyle ? 'translateY(0) scale(1)' : !visible ? 'translateY(30px) scale(0.95)' : undefined,
        transition: !flightStyle ? `all 0.7s cubic-bezier(0.16, 1, 0.3, 1) ${index * 120}ms` : undefined,
        ...(bento
          ? { ...THICK_TILE_STYLE, padding: '20px 22px' }
          : { border: `1px solid ${visible ? dim.color + '35' : '#1a1f3a'}`, padding: '20px 22px' }),
        ...flightStyle,
      }}
      onClick={onClick ? () => onClick(dim) : undefined}
    >
      {/* Corner glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: -30, right: -30, width: 100, height: 100,
          background: `radial-gradient(circle, ${dim.color}12 0%, transparent 70%)`,
        }}
      />

      <div className="flex justify-between items-start mb-2.5">
        <div className="flex items-center gap-2.5">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{
              background: dim.color,
              boxShadow: isHigh ? `0 0 10px ${dim.color}70` : `0 0 4px ${dim.color}30`,
            }}
          />
          <span className="text-sm font-semibold text-content">{dim.name}</span>
        </div>
        {dim.showNumber && dim.assessed ? (
          <div className="font-code text-2xl font-bold leading-none text-white">
            <AnimatedValue value={dim.value} delay={index * 120 + 200} animate={animate} />
            <span className="text-xs text-content-dim font-normal ml-0.5">/100</span>
          </div>
        ) : null}
      </div>

      <div
        className="inline-block text-xs font-semibold rounded-md mb-2.5"
        style={{
          color: dim.color,
          background: `${dim.color}12`,
          padding: '3px 10px',
        }}
      >
        {dim.label}
      </div>

      {/* The honest picture for this dimension's shape */}
      <div className="mb-2">
        <DimensionMeter dim={dim} size="compact" animate={animate} delay={index * 120 + 100} />
      </div>

      {/* Confidence bar — per-dimension confidence from Adaptive Engine.
          Research §10 recommends surfacing this as a trust-building signal. */}
      {dim.kind !== 'perUpload' && dim.assessed && (
        <ConfidenceBar
          confidence={dim.confidence}
          color={dim.color}
          index={index}
          visible={visible}
          animate={animate}
        />
      )}

      {/* THIS learner's result, not the dimension's definition. The definition is
          the caption; the body says what the answers actually showed. */}
      <p className="text-[13px] text-content-secondary leading-relaxed m-0">{dim.summary || dim.description}</p>
      {dim.summary && (
        <p className="text-[11px] text-content-dim leading-relaxed mt-1.5 mb-0">{dim.description}</p>
      )}
    </div>
  );
});

interface ConfidenceBarProps {
  confidence: number;
  color: string;
  index: number;
  visible: boolean;
  animate: boolean;
}

function ConfidenceBar({ confidence, color, index, visible, animate }: ConfidenceBarProps) {
  const tier = confidenceTier(confidence);
  const pct = Math.round(confidence * 100);

  const tierLabel =
    tier === 'strong' ? 'Strong' : tier === 'building' ? 'Building' : 'Low';

  return (
    <div className="flex items-center gap-2 mb-2.5" title={`Confidence: ${pct}% (${tierLabel})`}>
      <div className="h-[2px] rounded-sm bg-dark-subtle overflow-hidden flex-1">
        <div
          className="h-full rounded-sm"
          style={{
            background: `${color}80`,
            width: visible && animate ? `${pct}%` : '0%',
            transition: `width 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${index * 120 + 300}ms`,
            opacity: tier === 'low' ? 0.5 : 0.75,
          }}
        />
      </div>
      <span
        className="text-[9.5px] font-code tracking-wider uppercase text-content-dim shrink-0"
        style={{ letterSpacing: '0.06em' }}
      >
        conf {pct}%
      </span>
    </div>
  );
}
